import { formatDecimalPlace, formatNumber, parseNumber } from "common/formatters";
import React, { AriaAttributes, useCallback, useContext, useEffect, useRef, useState } from "react";
import NP from 'number-precision';
import "./NumberEditor.scss";
import { InputGroup, FormControl, Form } from "react-bootstrap";
import { FormContext } from "common/useFormContext";
import { getRemainder } from "common/decimal";
import classNames from "classnames";

type NumberEditorProps = {
    value?: Nullish<number | string>;
    max?: number;
    min?: number;
    step?: number;
    allowDecimals?: boolean;
    precision?: number;
    enforcePrecision?: boolean; //if precision needs to be enforced like in Price fields if 8 is entered then it has to be formatted as 8.00. Quantity field may not need that
    thousandSeparator?: boolean;
    isInvalidMessage?: string;
    onValueChange: ((value: number | null) => void);
    roundingCent?: boolean;
    className?: string;
    ignoreInputGroupClassName?: boolean;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    unit?: string;
    isInputLocked?: boolean;
    validationName?: string;
    type?: string;
};


function getWholeAndDecimalPart(value: Nullish<number | string>) {

    value = value ? value.toString().replaceAll(',', '') : '';

    let wholeNumberPart: string = '';
    let decimalPart: string = '';

    if (value.toString().includes('.')) {
        const returnValueArray = value.toString().split('.');
        wholeNumberPart = returnValueArray[0];
        decimalPart = returnValueArray[1] ?? ''
    }
    else
        wholeNumberPart = value;

    return { wholeNumberPart, decimalPart };
}

type NumberEditorState = { isEditing: boolean, value: Nullish<number | string>, isInvalid: boolean, initialValueOnFocus: Nullish<number | string> }
const NumberEditor: React.FC<NumberEditorProps & AriaAttributes> = (props: NumberEditorProps & AriaAttributes) => {

    const [state, setState] = useState<NumberEditorState>({ isEditing: false, value: props.value, isInvalid: false, initialValueOnFocus: null });

    const formContext = useContext(FormContext);

    function validateValue(val: Nullish<number | string>) {
        let isInvalid = false;

        if (typeof val === 'string') val = Number.parseFloat(val);

        if (!(val == null || Number.isNaN(val))) {
            if (props.allowDecimals) {
                if (val > (props.max ?? Number.POSITIVE_INFINITY))
                    isInvalid = true;

                if (val < (props.min ?? Number.NEGATIVE_INFINITY))
                    isInvalid = true;
            } else {
                if (val > (props.max ?? Number.MAX_VALUE))
                    isInvalid = true;

                if (val < (props.min ?? Number.MIN_VALUE))
                    isInvalid = true;
            }
        }
        setState(m => ({ ...m, isInvalid: isInvalid }))
        if (props.validationName != null)
            formContext.setField(props.validationName, !isInvalid);
    }


    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO we should find a better pattern here
        setState(m => ({ ...m, value: props.value }));
        validateValue(props.value);
    }, [props.value, props.min, props.max]);

    function increment() {
        if (props.step == null) {
            // note: currently increment/decrement can't be called if step is nullish, because the buttons aren't rendered
            // however, if we add arrow key support (a11y guideline) then we'll want to revisit this and consider how defaults should behave
            return;
        }
        if (props.value === "") {
            props.onValueChange(0);
            return;
        }
        const value: number = props.value == null ? 0
            : typeof props.value === 'string' ? Number.parseFloat(props.value)
            : props.value;
        if (props.roundingCent) {
            const remainder = getRemainder(value, props.step);
            const result = NP.plus(value, (NP.minus(props.step, remainder)));
            props.onValueChange(sanitizeValue(result))
        }
        else
            props.onValueChange(sanitizeValue(NP.plus(value, props.step)))
    }

    function decrement() {
        if (props.step == null) {
            // note: currently increment/decrement can't be called if step is nullish, because the buttons aren't rendered
            // however, if we add arrow key support (a11y guideline) then we'll want to revisit this and consider how defaults should behave
            return;
        }
        if (props.value === "") {
            props.onValueChange(0);
            return;
        }

        const value: number = props.value == null ? 0
            : typeof props.value === 'string' ? Number.parseFloat(props.value)
            : props.value;
        if (props.roundingCent) {
            const remainder = getRemainder(value, props.step);
            const result = NP.minus(value, (remainder === 0 ? props.step : remainder))
            props.onValueChange(sanitizeValue(result))
        }
        else
            props.onValueChange(sanitizeValue(NP.minus(value, props.step)))
    }

    function onChange(ev: React.ChangeEvent<HTMLInputElement>) {
        let value = ev.target.value ?? '';
        if (!props.allowDecimals && value.includes('.')) {
            value = value.replace('.', '');
        }
        setState(m => ({ ...m, isInvalid: false, value: value }))
    }

    function onKeyUp(ev: React.KeyboardEvent<HTMLInputElement>) {
        if (ev.key === "Enter") {
            onBlur();
            setState(m => ({ ...m, isEditing: true }))
        }
    }

    function onBlur() {
        const initialValue = state.initialValueOnFocus;
        setState(m => ({ ...m, isEditing: false, initialValueOnFocus: null }));
        let value = state.value;

        const { wholeNumberPart, decimalPart } = getWholeAndDecimalPart(value);
        if (props.precision != null && props.precision !== 0 && decimalPart !== '' && decimalPart.length > props.precision)
            value = truncateToPrecision(wholeNumberPart, decimalPart, props.precision)

        if (value != null && value !== "")
            value = value.toString().replaceAll(",", "");
        else
            value = "";

        const sanitized = sanitizeValue(props.allowDecimals ? Number.parseFloat(value) : Number.parseInt(value));

        // Only call onValueChange if the value actually changed from when focus was gained
        // This prevents unnecessary updates when tabbing through fields without editing
        // initialValue could be a number (from props) or a string (if user was typing)
        const initialAsNumber = typeof initialValue === 'number'
            ? initialValue
            : (initialValue != null && initialValue !== ""
                ? (props.allowDecimals ? Number.parseFloat(initialValue) : Number.parseInt(initialValue))
                : null);

        // Only trigger update if value actually changed
        if (sanitized !== initialAsNumber) {
            props.onValueChange(sanitized);
        }
    }

    function sanitizeValue(val: number): number | null {

        if (Number.isNaN(val)) return null;

        return Math.max(
            Math.min(
                val,
                props.max ?? Number.POSITIVE_INFINITY),
            props.min ?? Number.NEGATIVE_INFINITY
        );
    }

    function formattedValue() {
        let returnValue = state.isEditing ? state.value : props.value;
        if (returnValue == null || returnValue === '' || returnValue === 'NaN')
            return "";
        returnValue = returnValue.toString().replaceAll(/[^0-9.-]/g, ""); // strip out anything that's not a valid digit, decimal, or negative

        const { wholeNumberPart, decimalPart } = getWholeAndDecimalPart(returnValue);

        if (props.thousandSeparator) {
            if (returnValue.toString().includes('.')) {
                returnValue = (wholeNumberPart === '' || wholeNumberPart === '-' ? wholeNumberPart : formatNumber(Number.parseInt(wholeNumberPart))) + (decimalPart.length > 0 ? '.' + decimalPart : '.')
            }
            else {
                returnValue = wholeNumberPart === '' || wholeNumberPart === '-' ? wholeNumberPart : formatNumber(Number.parseInt(wholeNumberPart))
            }
        }

        //on editing - below logic is to do rounding of the decimal number when it crosses the precision. 
        //for eg. if precision is set to 2 and value entered is 8.525 then on entering 5 the value is rounded to 8.53... 8.521 will become 8.52
        //This logic executes only when the decimal part's length is more than the precision, otherwise while entering 0.0, it will round the value to to 0
        if (props.precision != null && props.precision !== 0 && decimalPart !== '' && decimalPart.length > props.precision)
            returnValue = truncateToPrecision(wholeNumberPart, decimalPart, props.precision);

        // on tab out  - if enforcePrecision is set to On then on tab out we have to set the precision even when there is no decimal point set. for eg. 
        // if 8 is entered, on tab out we have to make it 8.00. While enforcing parecision we just set to 2 decimal points
        if (!state.isEditing && props.precision !== 0 && props.enforcePrecision) {
            if (!returnValue.toString().includes('.')) {
                returnValue = formatDecimalPlace(Number.parseInt(wholeNumberPart))
            }
            else if (decimalPart.length < 2)
                returnValue = formatDecimalPlace(parseNumber(returnValue, 'en-US'));
        }

        return returnValue;
    }

    function truncateToPrecision(wholeNumberPart: string, decimalPart: string, precision: number): string {
        return `${wholeNumberPart}.${decimalPart.slice(0, precision)}`;
    }

    return (
        <InputGroup className={(props.ignoreInputGroupClassName ?? false) ? "" : "number-editor"}>
            <FormControl
                type={props.type || 'tel'}
                style={{ backgroundImage: "none" }}
                value={formattedValue()}
                max={props.max}
                min={props.min}
                step={props.step}

                className={props.className}
                required={props.required}
                disabled={props.disabled}
                isInvalid={state.isInvalid}
                placeholder={props.placeholder}

                onChange={props.isInputLocked ? undefined : onChange}
                onKeyUp={props.isInputLocked ? undefined : onKeyUp}
                onFocus={() => setState(m => ({ ...m, isEditing: true, initialValueOnFocus: m.value }))}
                onBlur={props.isInputLocked ? undefined : onBlur}

                aria-label={props["aria-label"]}
            />
            {(props.step != null && !props.disabled) && <InputGroup.Append className="step-buttons">
                <div className="btn-group-vertical">
                    <button type="button" onClick={(ev: React.MouseEvent) => { increment(); ev.preventDefault(); ev.stopPropagation() }}>
                        <i className="material-icons">expand_less</i>
                    </button>
                    <button type="button" onClick={(ev: React.MouseEvent) => { decrement(); ev.preventDefault(); ev.stopPropagation() }}>
                        <i className="material-icons">expand_more</i>
                    </button>
                </div>
            </InputGroup.Append>}
            {props.unit && <InputGroup.Append>
                <InputGroup.Text>{props.unit}</InputGroup.Text>
            </InputGroup.Append>}
            {state.isInvalid && <Form.Control.Feedback type="invalid">
                {props.isInvalidMessage}
            </Form.Control.Feedback>}
        </InputGroup>
    );
}

export default React.memo(NumberEditor);

type NumberEditorLiteProps = {
    value: string | number | null;
    min?: number;
    max?: number;
    step?: number;
    precision?: number;
    onValueChange: (value: number | null) => void;
    className?: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
}

function normalizeLiteValue(value: string | number | null): number | null {
    if (value == null) return null;
    if (typeof value === 'number') return value;
    const parsed = Number.parseFloat(value.replace(/\.$/, '')); // cleanup trailing decimal
    return Number.isNaN(parsed) ? null : parsed;
}

function truncateLiteTextToPrecision(text: string, precision: number | undefined): string {
    if (precision == null ) return text;

    const { wholeNumberPart, decimalPart } = getWholeAndDecimalPart(text);
    if (decimalPart === '') {
        return text;
    } else {
        return `${wholeNumberPart}.${decimalPart.slice(0, precision)}`;
    }
}

/**
 * lighter weight alternative to the NumberEditor, utilizing native type=number input and spinner,
 * suitable for use in large grids
 * 
 * intentionally missing the following features from NumberEditor, to limit this to a single HTML element
 * - built-in validation feedback
 * - thousandsSeparator
 * - units
 * 
 * additional missing (possible, but not needed at time of implementation):
 * - allowDecimals (always allows decimals, whereas NumberEditor defaults to false)
 * - enforcePrecision
 * - isInputLocked
 * - roundingCent
 */
export const NumberEditorLite = React.memo(function NumberEditorLite(props: NumberEditorLiteProps) {
    // destructure props used in dependency arrays (to suppress lint warnings)
    const { min, max, onValueChange, precision, step, value } = props;

    const prevValueRef = useRef(value?.toString() ?? '');
    const inputRef = useRef<HTMLInputElement>(null);
    const preventNextSpinnerEventRef = useRef(false);

    // input uncontrolled so half-typed values survive (eg `-` or `0.`)
    // sync with props.value when normalized values differ
    useEffect(function syncValueFromProps() {
        if (inputRef.current == null) return;
        if (normalizeLiteValue(inputRef.current.value) !== normalizeLiteValue(value)) {
            inputRef.current.value = value?.toString() ?? '';
            prevValueRef.current = inputRef.current.value;
        };
    }, [precision, value]);

    const syncValueToProps = useCallback(function syncValueToProps() {
        const input = inputRef.current;
        if (input == null) return;

        let newValue = normalizeLiteValue(input.value);
        if (newValue != null) {
            newValue = Math.min(Math.max(newValue, min ?? -Infinity), max ?? Infinity);
        }

        // reset input to the original value to preserve formatting
        // if props.value changes, this will be updated via syncValueFromProps effect
        input.value = value?.toString() ?? ''
        prevValueRef.current = input.value;

        if (newValue !== normalizeLiteValue(value)) onValueChange(newValue);
    }, [min, max, onValueChange, value]);

    const onChange = useCallback(function onChange(event: React.ChangeEvent<HTMLInputElement>) {
        const input = event.target;
        const isSpinnerEvent =  !(event.nativeEvent instanceof InputEvent) || !event.nativeEvent.inputType;
        if (isSpinnerEvent) {
            // prevent spinner events triggered by mouse wheel (see onWheel)
            if (preventNextSpinnerEventRef.current) {
                preventNextSpinnerEventRef.current = false;
                input.value = prevValueRef.current;
                return;
            }
            // override spinner behavior for consistency with NumberEditor
            const stepUp = ((normalizeLiteValue(input.value) ?? 0) > (normalizeLiteValue(prevValueRef.current) ?? 0));
            const newValue = NP.plus((normalizeLiteValue(prevValueRef.current) ?? 0), (step ?? 1) * (stepUp ? 1 : -1));
            input.value = newValue.toString();
            syncValueToProps();
        } else {
            // enforce precision
            let text = input.value;
            text = truncateLiteTextToPrecision(text, precision);
            if (text !== input.value) input.value = text;
        }
        prevValueRef.current = input.value;
    }, [precision, step, syncValueToProps]);

    // this feels hacky and brittle (Enter should submit a form, which could cause timing issues with state update)
    // but copied for consistent behavior with NumberEditor
    const onKeyUp = useCallback(function onKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        preventNextSpinnerEventRef.current = false;
        if (event.key === "Enter") {
            syncValueToProps();
        }
    }, [syncValueToProps])

    const onWheel = useCallback(function onWheel() {
        preventNextSpinnerEventRef.current = true;
    }, []);

    return (
        <input
            ref={inputRef}
            className={classNames(
                'form-control',
                'number-editor-lite',
                props.disabled || step == null ? 'hide-spinner' : 'show-spinner',
                props.className,
            )}
            type="number"
            min={props.min}
            max={props.max}
            step={props.step}
            required={props.required}
            disabled={props.disabled}
            placeholder={props.placeholder}
            onChange={onChange}
            onBlur={syncValueToProps}
            onKeyUp={onKeyUp}
            onWheel={onWheel}
        />
    )
});